import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Form, Input, Space, Typography } from "antd";
import {
  ArrowLeftOutlined,
  LoginOutlined,
  MailOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import "../Login/login.scss";
import {
  getUserDetails,
  resendLoginCode,
  verifyLoginCode,
} from "../../../services/authService";
import {
  clearLoginTwoFactorPendingState,
  mergeLoginTwoFactorPendingState,
  readLoginTwoFactorPendingState,
  secondsUntilResend,
  writeLoginTwoFactorPendingState,
} from "../../../services/loginTwoFactorSession";
import {
  getCompanyMe,
  persistCorporateUser,
} from "../../../features/corporate/services/corporateAuthService";
import { CORPORATE_ROUTES } from "../../../features/corporate/constants/corporateConstants";
import { ROUTE_IDS } from "../../../i18n/routeIds";
import { PRODUCT_IDS } from "../../../i18n/productContext";
import { useAppContext } from "../../../contexts/AppContext";
import { useDashboardNavigate } from "../../../i18n/hooks/useDashboardNavigate";
import { useProductContext } from "../../../i18n/providers/ProductContextProvider";
import { useLocalePath } from "../../../contexts/LocalePathContext";
import {
  resolveLoginPostLoginTarget,
  traceLoginLocaleFlow,
} from "../../../i18n/loginLocaleFlow";
import { HOTELSRIVER_OFFICIAL_LOGO_URL } from "../../../utils/hotelsRiverBrandLogo";
import { getMonthlyLoginBackground } from "../../../utils/loginMonthlyBackgrounds";
import { useTranslation } from "react-i18next";

const { Text, Title } = Typography;

function readPendingState() {
  return readLoginTwoFactorPendingState();
}

function writePendingState(nextState) {
  writeLoginTwoFactorPendingState(nextState);
}

function secondsUntil(value) {
  return secondsUntilResend(value);
}

export default function LoginVerifyPage() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dashNav = useDashboardNavigate();
  const { appLogin } = useAppContext();
  const { locale, product } = useProductContext();
  const { localizePath } = useLocalePath();
  const { t } = useTranslation();
  const [pending, setPending] = useState(() => readPendingState());
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(() => secondsUntil(pending?.resend_available_at));
  const [imageReady, setImageReady] = useState(false);

  const background = useMemo(() => getMonthlyLoginBackground(), []);
  const verificationSession = pending?.verification_session;
  const maskedEmail = pending?.masked_email || "";
  const canResend = useMemo(() => timer <= 0 && !resending, [timer, resending]);

  useEffect(() => {
    setImageReady(false);
    if (!background?.url) return undefined;
    const img = new Image();
    img.decoding = "async";
    let cancelled = false;
    img.onload = () => {
      if (!cancelled) setImageReady(true);
    };
    img.onerror = () => {
      if (!cancelled) setImageReady(true);
    };
    img.src = background.url;
    return () => {
      cancelled = true;
    };
  }, [background?.url]);

  useEffect(() => {
    if (!verificationSession) {
      const backPath = pending?.back_path || localizePath("/login");
      navigate(backPath, { replace: true });
      return;
    }
    if (pending?.portal === "corporate") {
      navigate(CORPORATE_ROUTES.LOGIN_VERIFY, { replace: true });
    }
  }, [
    verificationSession,
    navigate,
    localizePath,
    pending?.back_path,
    pending?.portal,
  ]);

  useEffect(() => {
    setTimer(secondsUntil(pending?.resend_available_at));
  }, [pending?.resend_available_at]);

  useEffect(() => {
    if (timer <= 0) return undefined;
    const id = window.setInterval(() => {
      setTimer((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [timer]);

  const clearAndBackToLogin = () => {
    clearLoginTwoFactorPendingState();
    navigate(pending?.back_path || localizePath("/login"), { replace: true });
  };

  const redirectAfterLogin = async () => {
    if (pending?.portal === "corporate") {
      const me = await getCompanyMe();
      if (me?.ok) {
        const user = persistCorporateUser(me);
        appLogin(user);
        navigate(CORPORATE_ROUTES.DASHBOARD, { replace: true });
        return;
      }
      setError("שגיאה בטעינת נתוני החברה");
      return;
    }

    const user = getUserDetails();
    if (!user) {
      setError("שגיאה בטעינת נתוני המשתמש");
      return;
    }

    appLogin(user);
    const userType = user.usertype;
    const target = resolveLoginPostLoginTarget(userType, locale);
    traceLoginLocaleFlow({
      loginPath: window.location.pathname,
      resolvedLocale: locale,
      product,
      postLoginTarget: target.path,
      postLoginRouteId: target.routeId,
      phase: "post_login_2fa",
    });

    if (userType === "clients") {
      dashNav.to(ROUTE_IDS.SECURE_CLIENT_ROOT, {
        product: PRODUCT_IDS.AGENT,
        locale,
      });
    } else if (userType === "administrators") {
      dashNav.to(ROUTE_IDS.ADMINISTRATION, {
        product: PRODUCT_IDS.ADMIN,
        locale,
      });
    } else {
      dashNav.to(target.routeId, {
        product: PRODUCT_IDS.AGENT,
        locale,
      });
    }
  };

  const handleVerify = async ({ code }) => {
    setError("");
    setLoading(true);
    try {
      const res = await verifyLoginCode(verificationSession, code);
      if (res?.status === "ok") {
        clearLoginTwoFactorPendingState();
        await redirectAfterLogin();
      } else {
        setError(res?.message || "Invalid or expired verification code");
      }
    } catch (err) {
      console.error("Login 2FA verify error:", err);
      setError("Invalid or expired verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setError("");
    setResending(true);
    try {
      const res = await resendLoginCode(verificationSession);
      if (res?.status === "ok") {
        const nextPending = mergeLoginTwoFactorPendingState(pending, {
          verification_session: res.verification_session || verificationSession,
          masked_email: res.masked_email || maskedEmail,
          can_resend: res.can_resend,
          resend_available_at: res.resend_available_at,
        });
        writePendingState(nextPending);
        setPending(nextPending);
        form.setFieldsValue({ code: "" });
      } else {
        if (res?.resend_available_at) {
          const nextPending = mergeLoginTwoFactorPendingState(pending, {
            can_resend: res.can_resend,
            resend_available_at: res.resend_available_at,
          });
          writePendingState(nextPending);
          setPending(nextPending);
        }
        setError(res?.message || "לא ניתן לשלוח קוד חדש כרגע");
      }
    } catch (err) {
      console.error("Login 2FA resend error:", err);
      setError("לא ניתן לשלוח קוד חדש כרגע");
    } finally {
      setResending(false);
    }
  };

  if (!verificationSession) {
    return null;
  }

  return (
    <div className="login-page" data-testid="agent-login-verify-page">
      <div
        className="login-page__bg"
        role="img"
        aria-label={`${background.monthNameHe}: ${background.labelHe}`}
      >
        <div
          className={[
            "login-page__bg-image",
            imageReady ? "is-visible" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{ backgroundImage: `url("${background.url}")` }}
          data-bg-id={background.id}
        />
        <div className="login-page__bg-wash" aria-hidden />
      </div>

      <div className="login-page__stage">
        <div className="login-page__brand">
          <img
            className="login-page__logo"
            src={HOTELSRIVER_OFFICIAL_LOGO_URL}
            alt="HotelsRiver"
          />
          <p className="login-page__tagline">{t("login.tagline")}</p>
        </div>

        <main className="login-page__card">
          <div className="login-page__form-head">
            <Title level={2}>בדוק את תיבת המייל שלך</Title>
            <Text className="login-page__welcome">
              שלחנו קוד אימות אל {maskedEmail || "המייל שלך"}
            </Text>
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              closable
              showIcon
              onClose={() => setError("")}
              style={{ marginBottom: 20, borderRadius: 8 }}
            />
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={handleVerify}
            size="large"
          >
            <Form.Item
              name="code"
              label="קוד אימות"
              rules={[
                { required: true, message: "יש להזין קוד אימות" },
                { pattern: /^\d{6}$/, message: "יש להזין קוד בן 6 ספרות" },
              ]}
            >
              <Input
                inputMode="numeric"
                maxLength={6}
                prefix={<MailOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                placeholder="______"
                autoComplete="one-time-code"
                style={{ letterSpacing: 8, textAlign: "center" }}
                onChange={(event) => {
                  const digits = event.target.value.replace(/\D/g, "").slice(0, 6);
                  form.setFieldsValue({ code: digits });
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 16, marginTop: 8 }}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<LoginOutlined />}
                loading={loading}
                block
                className="login-page__submit"
              >
                אימות
              </Button>
            </Form.Item>
          </Form>

          <div className="login-page__verify-footer">
            <Text type="secondary">לא קיבלת את הקוד?</Text>
            <Space
              direction="vertical"
              size={8}
              style={{ width: "100%", marginTop: 12 }}
            >
              <Button
                icon={<ReloadOutlined />}
                onClick={handleResend}
                loading={resending}
                disabled={!canResend}
                block
                className="login-page__resend"
              >
                {timer > 0
                  ? `שלח שוב קוד בעוד ${timer} שניות`
                  : "שלח שוב קוד"}
              </Button>
              <Button
                icon={<ArrowLeftOutlined />}
                type="link"
                onClick={clearAndBackToLogin}
                className="login-page__forgot"
              >
                חזרה להתחברות
              </Button>
            </Space>
          </div>
        </main>
      </div>

      <p className="login-page__credit">
        <span>
          צילום:{" "}
          <a
            href={background.photographerUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {background.photographer}
          </a>
          {" · "}
          <a
            href={background.unsplashUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Unsplash
          </a>
        </span>
        <span className="login-page__credit-license">{background.license}</span>
      </p>
    </div>
  );
}
